import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../useAuth';
import { apiGet } from '../api';
import StatTile from '../components/StatTile';
import GrowthChart from '../components/GrowthChart';
import AllocationDonut from '../components/AllocationDonut';
import HoldingsTable from '../components/HoldingsTable';
import InsightsPanel from '../components/InsightsPanel';
import Sidebar from '../components/Sidebar';
import { page, track } from '../lib/analytics';

const fmtUSD = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);

const AGGREGATE_OPTIONS = [
  { id: 'position', label: 'Position' },
  { id: 'sector', label: 'Sector' },
  { id: 'subSector', label: 'Sub-sector' },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState(null);
  const [holdings, setHoldings] = useState(null);
  const [insight, setInsight] = useState(null);
  const [err, setErr] = useState(null);
  const [aggregateBy, setAggregateBy] = useState(() => {
    try {
      return localStorage.getItem('shrewdhedge.aggregateBy') || 'position';
    } catch {
      return 'position';
    }
  });

  function handleAggregateChange(id) {
    setAggregateBy(id);
    try {
      localStorage.setItem('shrewdhedge.aggregateBy', id);
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiGet('/api/me'),
      apiGet('/api/plaid/items'),
      apiGet('/api/snaptrade/connections'),
      apiGet('/api/portfolio/summary'),
      apiGet('/api/portfolio/history?days=90'),
      apiGet('/api/portfolio/holdings'),
      apiGet('/api/insights'),
    ])
      .then(([meData, plaidData, snaptradeData, summaryData, historyData, holdingsData, insightsData]) => {
        if (cancelled) return;
        setMe(meData);
        // Combine both providers into a single connections list so the
        // dashboard reads accurately whether the user has Plaid items,
        // SnapTrade connections, or both. Each row keeps a `provider` tag.
        const combined = [
          ...(plaidData.items || []).map((i) => ({ ...i, provider: 'plaid' })),
          ...(snaptradeData.connections || [])
            .filter((c) => !c.disabled)
            .map((c) => ({ ...c, provider: 'snaptrade' })),
        ];
        setItems(combined);
        setSummary(summaryData);
        setHistory(historyData.points);
        setHoldings(holdingsData.holdings);
        setInsight(insightsData.insight);
        page('dashboard_view', {
          items_count: combined.length,
          holdings_count: holdingsData.holdings.length,
        });
        if (insightsData.insight) {
          track('insights_view', {
            model: insightsData.insight.model,
            risk_count: insightsData.insight.content?.risk_flags?.length || 0,
            opp_count: insightsData.insight.content?.opportunities?.length || 0,
          });
        }
      })
      .catch((e) => !cancelled && setErr(e.message));
    return () => {
      cancelled = true;
    };
  }, []);

  // Three-state load: null = still fetching (skeleton), [] = loaded but no
  // accounts (welcome empty state), [...] = loaded with accounts (full UI).
  const loading = items === null;
  const hasItems = items && items.length > 0;
  const dayTone = summary?.day_change_value > 0
    ? 'positive'
    : summary?.day_change_value < 0
      ? 'negative'
      : 'neutral';

  const allocationView = useMemo(() => {
    if (!holdings || holdings.length === 0) return { allocation: {}, dollars: {} };

    let total = 0;
    const dollars = {};
    for (const h of holdings) {
      const name = h.sector || 'Unclassified';
      const v = Number(h.institution_value);
      if (!Number.isFinite(v)) continue;
      dollars[name] = (dollars[name] || 0) + v;
      total += v;
    }

    const allocation = {};
    for (const [name, v] of Object.entries(dollars)) {
      allocation[name] = total > 0 ? (v / total) * 100 : 0;
    }
    return { allocation, dollars };
  }, [holdings]);

  return (
    <div className="min-h-screen flex bg-neutral-950 text-neutral-100">
      <Sidebar />
      <main className="flex-1 px-6 py-10">
        <div className="max-w-5xl mx-auto">

        {err && (
          <div className="rounded border border-red-900 bg-red-950/40 px-4 py-3 mb-6">
            <p className="text-sm text-red-300">{err}</p>
          </div>
        )}

        {loading ? (
          <DashboardSkeleton />
        ) : !hasItems ? (
          <div className="rounded border border-neutral-800 bg-neutral-900 px-6 py-8 text-center">
            <h2 className="text-2xl font-semibold mb-2">
              Welcome{me?.email ? `, ${me.email}` : ''}.
            </h2>
            <p className="text-neutral-400 mb-4">
              Connect an investment account to see your portfolio, growth, and
              AI insights.
            </p>
            <Link
              to="/connect"
              className="inline-block px-5 py-2.5 rounded bg-indigo-500 hover:bg-indigo-400 text-white font-medium"
            >
              Connect an account
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              <StatTile
                label="Portfolio value"
                value={fmtUSD(summary?.total_value || 0)}
                sub={summary?.as_of ? `as of ${summary.as_of}` : 'pending sync'}
              />
              <StatTile
                label="Day change"
                value={`${summary?.day_change_value >= 0 ? '+' : ''}${fmtUSD(summary?.day_change_value || 0)}`}
                sub={`${summary?.day_change_pct?.toFixed(2) || '0.00'}%`}
                tone={dayTone}
              />
              <StatTile
                label="Connected accounts"
                value={items.length}
                sub={items.map((i) => i.institution_name).filter(Boolean).join(', ') || '—'}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
              <div>
                <h3 className="text-sm uppercase tracking-wide text-neutral-500 mb-2">
                  Portfolio value
                </h3>
                <GrowthChart points={history} />
              </div>
              <div>
                <h3 className="text-sm uppercase tracking-wide text-neutral-500 mb-2">
                  Allocation by sector
                </h3>
                <AllocationDonut
                  allocation={allocationView.allocation}
                  dollars={allocationView.dollars}
                  holdings={holdings}
                />
              </div>
            </div>

            <div className="flex items-center justify-start mb-3">
              <div
                role="tablist"
                aria-label="Group dashboard by"
                className="inline-flex rounded border border-neutral-800 bg-neutral-900 p-0.5 text-xs"
              >
                {AGGREGATE_OPTIONS.map((opt) => {
                  const active = aggregateBy === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => handleAggregateChange(opt.id)}
                      className={`px-3 py-1 rounded transition-colors ${
                        active
                          ? 'bg-neutral-800 text-neutral-100'
                          : 'text-neutral-400 hover:text-neutral-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <h3 className="text-sm uppercase tracking-wide text-neutral-500 mb-2">
              AI insights
            </h3>
            <InsightsPanel insight={insight} />

            <h3 className="text-sm uppercase tracking-wide text-neutral-500 mb-2 mt-6">
              Holdings
            </h3>
            <HoldingsTable holdings={holdings} aggregateBy={aggregateBy} />

            <div className="mt-6 flex justify-between text-xs text-neutral-500">
              <div className="flex gap-4">
                <Link to="/connect" className="text-indigo-400 hover:text-indigo-300">
                  + Connect another account
                </Link>
                <Link to="/research" className="text-indigo-400 hover:text-indigo-300">
                  Deep research a ticker →
                </Link>
              </div>
              <span>
                {items.length} institution{items.length === 1 ? '' : 's'} ·
                {' '}
                {items.filter((i) => i.last_sync_at).length} synced
              </span>
            </div>
          </>
        )}

        <details className="mt-10">
          <summary className="text-xs text-neutral-500 cursor-pointer">
            Debug
          </summary>
          <pre className="mt-2 rounded bg-neutral-900 border border-neutral-800 px-4 py-3 text-xs text-neutral-400 overflow-auto">
            {JSON.stringify({ amplify: user, server: me, summary }, null, 2)}
          </pre>
        </details>
        </div>
      </main>
    </div>
  );
}

// Shimmering placeholder shown during the initial dashboard load. The shape
// mirrors the real layout so the page doesn't reflow when data arrives:
// 3 stat tiles, two side-by-side charts, an insights panel, and two account
// section cards. Tailwind's animate-pulse is the standard loading indicator.
function DashboardSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="rounded border border-neutral-800 bg-neutral-900 px-4 py-3 h-[88px]"
          >
            <div className="h-3 w-24 bg-neutral-800 rounded mb-3" />
            <div className="h-6 w-32 bg-neutral-800 rounded mb-2" />
            <div className="h-3 w-20 bg-neutral-800 rounded" />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
        {[0, 1].map((i) => (
          <div key={i}>
            <div className="h-3 w-24 bg-neutral-800 rounded mb-2" />
            <div className="h-64 rounded border border-neutral-800 bg-neutral-900" />
          </div>
        ))}
      </div>

      <div className="h-3 w-24 bg-neutral-800 rounded mb-2" />
      <div className="rounded border border-neutral-800 bg-neutral-900 p-4 mb-6 space-y-3">
        <div className="h-4 w-3/4 bg-neutral-800 rounded" />
        <div className="h-4 w-2/3 bg-neutral-800 rounded" />
        <div className="h-4 w-1/2 bg-neutral-800 rounded" />
      </div>

      <div className="h-3 w-20 bg-neutral-800 rounded mb-2 mt-6" />
      <div className="space-y-6">
        {[0, 1].map((i) => (
          <div key={i} className="space-y-3">
            <div className="flex items-center justify-between border-b border-neutral-800 pb-2">
              <div className="h-4 w-32 bg-neutral-800 rounded" />
              <div className="h-4 w-24 bg-neutral-800 rounded" />
            </div>
            <div className="rounded border border-neutral-800 bg-neutral-900">
              <div className="px-4 py-3 border-b border-neutral-800">
                <div className="h-4 w-1/2 bg-neutral-800 rounded" />
              </div>
              <div className="p-4 space-y-2">
                {[0, 1, 2].map((j) => (
                  <div key={j} className="h-5 bg-neutral-800/70 rounded" />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
